/* ========================================
   COLLECTION FILTER SYSTEM - UI MODULES
   ========================================
   
   This file contains UI-related modules:
   - FilterOverlay: Sidebar show/hide, scroll locking
   - LoadingManager: Loading overlay management
   - SelectedFiltersManager: Filter chips display
   - EventHandlers: Event binding for UI interactions
   
   Dependencies: CONFIG, Utils (from collection-filter.js)
   ======================================== */

(function() {
  'use strict';
  
  // Wait for CONFIG and Utils to be available
  if (typeof window.FilterUtils === 'undefined') {
    console.error('[FilterUI] FilterUtils not found. Make sure collection-filter.js loads first.');
    return;
  }
  
  const { CONFIG, Utils } = window.FilterUtils;

  // ========================================
  // SCREEN READER ANNOUNCEMENTS (WCAG)
  // ========================================

  const Announcements = {
    getRegion() {
      return document.getElementById('filter-status-announcer') ||
             document.getElementById('filter-status-live');
    },
    announce(message, delayMs = 100) {
      const region = this.getRegion();
      if (!region) return;
      const clear = () => {
        region.textContent = '';
      };
      const set = () => {
        region.textContent = message;
      };
      clear();
      setTimeout(() => {
        set();
        setTimeout(clear, 1000);
      }, delayMs);
    }
  };

  // ========================================
  // FILTER OVERLAY MANAGEMENT
  // ========================================
  
  const FilterOverlay = {
    setFreeze() {
      const blurCover = document.querySelector(CONFIG.selectors.blurCover);
      if (blurCover) {
        blurCover.classList.remove('hidden');
        blurCover.style.display = 'block';
        blurCover.style.position = 'fixed';
        blurCover.style.top = '0';
        blurCover.style.left = '0';
        blurCover.style.width = '100%';
        blurCover.style.height = '100vh';
        blurCover.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        blurCover.style.zIndex = '999998';
      }
    },

    releaseFreeze() {
      const blurCover = document.querySelector(CONFIG.selectors.blurCover);
      if (blurCover) {
        blurCover.classList.add('hidden');
        blurCover.style.display = 'none';
      }
    },

    lockBodyScroll() {
      // Store the current scroll position
      const scrollY = window.scrollY || window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.classList.add('filter-open');
      document.documentElement.classList.add('filter-open');
      if (FilterOverlay._isIOS()) {
        document.documentElement.classList.add('filter-ios');
      }
      // Store scroll position for restoration
      document.body.setAttribute('data-scroll-position', scrollY);
    },

    _isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    },

    unlockBodyScroll() {
      // Restore scroll position
      const scrollY = document.body.getAttribute('data-scroll-position') || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('filter-open');
      document.documentElement.classList.remove('filter-open');
      document.documentElement.classList.remove('filter-ios');
      document.body.removeAttribute('data-scroll-position');
      // Restore scroll position
      window.scrollTo(0, parseInt(scrollY, 10));
    },

    show() {
      const filter = document.querySelector(CONFIG.selectors.collectionFilter);
      const showFilterBtn = document.querySelector(CONFIG.selectors.showFilterBtn);
      if (filter) {
        filter.classList.add('show');
        filter.removeAttribute('inert');
        filter.setAttribute('aria-hidden', 'false');
        this.lockBodyScroll();
        if (showFilterBtn) {
          showFilterBtn.setAttribute('aria-expanded', 'true');
        }
        Announcements.announce('Filters opened. Use Escape to close.');
        this._focusTrapStart = document.activeElement;
        document.addEventListener('keydown', this._boundFocusTrap = this._focusTrap.bind(this));
        if (typeof window.FilterSelectionManager !== 'undefined') {
          requestAnimationFrame(() => {
            window.FilterSelectionManager.initializeAppliedFilters();
            window.FilterSelectionManager.updateApplyButton();
          });
        }
        // Move focus into the filter drawer after panel is visible (so Enter on "Show Filter" lands inside drawer)
        const focusableSelector = 'button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';
        const moveFocusToDrawer = () => {
          const drawer = filter.querySelector('.collection-filter__groups-wrapper');
          const scope = drawer || filter;
          const focusable = Array.from(scope.querySelectorAll(focusableSelector)).filter(el => el.getAttribute('aria-hidden') !== 'true');
          if (focusable.length > 0) {
            focusable[0].focus();
          } else {
            const fallback = filter.querySelector(focusableSelector);
            if (fallback) fallback.focus();
          }
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(moveFocusToDrawer);
        });
        setTimeout(moveFocusToDrawer, 120);
      } else {
        Utils.error('Collection filter element not found');
      }
    },

    _focusTrap(e) {
      if (e.key !== 'Tab') return;
      const filter = document.querySelector(CONFIG.selectors.collectionFilter);
      if (!filter || !filter.classList.contains('show')) return;
      const selector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
      const elements = filter.querySelectorAll(selector);
      const focusable = Array.prototype.filter.call(elements, function(el) {
        return el.offsetParent !== null &&
          el.getAttribute('aria-hidden') !== 'true' &&
          !el.hasAttribute('inert');
      });
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },

    hide(skipFocusMove) {
      const filter = document.querySelector(CONFIG.selectors.collectionFilter);
      const showFilterBtn = document.querySelector(CONFIG.selectors.showFilterBtn);
      if (filter) {
        filter.classList.remove('show');
        filter.setAttribute('inert', '');
        filter.setAttribute('aria-hidden', 'true');
        this.releaseFreeze();
        this.unlockBodyScroll();
        Utils.setLocalStorage(CONFIG.localStorageKeys.filterOpen, 'false');
        if (showFilterBtn) {
          showFilterBtn.setAttribute('aria-expanded', 'false');
          // Only move focus to Show Filter button if not skipping (e.g., when Apply button was clicked)
          if (!skipFocusMove) {
            showFilterBtn.focus();
          }
        }
        Announcements.announce('Filters closed.');
        document.removeEventListener('keydown', this._boundFocusTrap);
        this._boundFocusTrap = null;
      }
    },

    toggle() {
      const filter = document.querySelector(CONFIG.selectors.collectionFilter);
      if (filter) {
        if (filter.classList.contains('show')) {
          this.hide();
        } else {
          this.show();
        }
      }
    }
  };

  // ========================================
  // LOADING MANAGER
  // ========================================
  
  const LoadingManager = {
    showFilterLoading() {
      const overlay = document.querySelector(CONFIG.selectors.filterLoadingOverlay);
      const filter = document.querySelector(CONFIG.selectors.collectionFilter);
      if (overlay) {
        overlay.classList.add('show');
        overlay.setAttribute('aria-hidden', 'false');
        overlay.setAttribute('aria-busy', 'true');
      }
      if (filter) {
        filter.classList.add('loading');
      }
      Announcements.announce('Updating filters.');
    },

    hideFilterLoading() {
      const overlay = document.querySelector(CONFIG.selectors.filterLoadingOverlay);
      const filter = document.querySelector(CONFIG.selectors.collectionFilter);
      
      if (overlay) {
        overlay.classList.remove('show');
        overlay.setAttribute('aria-hidden', 'true');
      }
      if (filter) {
        filter.classList.remove('loading');
      }
    },

    showCollectionLoading() {
      const overlay = document.querySelector(CONFIG.selectors.collectionLoadingOverlay);
      const wrapper = document.querySelector(CONFIG.selectors.productsWrapper);
      const main = document.querySelector(CONFIG.selectors.collectionMain);
      if (overlay) {
        overlay.classList.add('show');
        overlay.setAttribute('aria-hidden', 'false');
      }
      if (wrapper) {
        wrapper.classList.add('loading');
      }
      if (main) {
        main.classList.add('updating');
      }
      Announcements.announce('Loading results.');
    },

    hideCollectionLoading() {
      const overlay = document.querySelector(CONFIG.selectors.collectionLoadingOverlay);
      const wrapper = document.querySelector(CONFIG.selectors.productsWrapper);
      const main = document.querySelector(CONFIG.selectors.collectionMain);
      
      if (overlay) {
        overlay.classList.remove('show');
        overlay.setAttribute('aria-hidden', 'true');
      }
      if (wrapper) {
        wrapper.classList.remove('loading');
      }
      if (main) {
        main.classList.remove('updating');
      }
    },

    showAllLoading() {
      this.showFilterLoading();
      this.showCollectionLoading();
    },

    hideAllLoading() {
      // Batch DOM updates using requestAnimationFrame for optimal performance
      requestAnimationFrame(() => {
        this.hideFilterLoading();
        this.hideCollectionLoading();
      });
    }
  };

  // ========================================
  // SELECTED FILTERS MANAGEMENT
  // ========================================
  
  const SelectedFiltersManager = {
    init() {
      this.bindEvents();
      this.updateDisplay();
    },

    bindEvents() {
      // Clear all filters button
      const clearAllBtn = document.querySelector(CONFIG.selectors.clearAllFiltersBtn);
      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', this.clearAllFilters.bind(this));
      }
    },

    getActiveFilters() {
      const filters = [];
      
      // Get sidebar filters (active tags)
      const activeSidebarTags = document.querySelectorAll('.collection-filter__tag--active');
      activeSidebarTags.forEach(tag => {
        const link = tag.querySelector('a');
        if (link) {
          const text = link.textContent.trim();
          const category = tag.closest('.collection-filter__group')?.querySelector('.collection-filter__group-title span')?.textContent?.trim() || 'Filter';
          filters.push({
            type: 'sidebar',
            category: category,
            value: text,
            element: tag,
            id: `sidebar-${category}-${text}`.replace(/\s+/g, '-').toLowerCase()
          });
        }
      });

      // Get tab filters (active collection boxes)
      const activeTabFilters = document.querySelectorAll('.filter-collection-box.active--filters');
      activeTabFilters.forEach(box => {
        const text = box.textContent.trim();
        filters.push({
          type: 'tab',
          category: 'Collection',
          value: text,
          element: box,
          id: `tab-${text}`.replace(/\s+/g, '-').toLowerCase()
        });
      });

      // Fallback: derive filters from checked facet inputs (for SSR pagination pages)
      const checkedFacets = document.querySelectorAll('input[type="checkbox"][name^="filter."]:checked, input[type="radio"][name^="filter."]:checked');
      checkedFacets.forEach(input => {
        const labelText =
          input.closest('label')?.textContent?.trim() ||
          input.getAttribute('value') ||
          '';
        if (!labelText) return;

        // Try to get group/category label
        const groupEl = input.closest('[data-facet-name]') ||
                        input.closest('.facets__disclosure') ||
                        input.closest('.collection-filter__group');
        const category =
          groupEl?.getAttribute('data-facet-name') ||
          groupEl?.querySelector('.collection-filter__group-title span, .facets__selected, summary')?.textContent?.trim() ||
          'Filter';

        const id = `sidebar-${category}-${labelText}`.replace(/\s+/g, '-').toLowerCase();

        // Avoid duplicates if already collected
        if (filters.find(f => f.id === id)) {
          return;
        }

        filters.push({
          type: 'sidebar',
          category,
          value: labelText,
          element: input,
          id
        });
      });

      // Fallback: detect filters from URL path segments (e.g., /collections/best-selling-nails/opaque+semi-transparent)
      // Build lookup map once to avoid repeated DOM queries in loops
      const pathParts = window.location.pathname.split('/').filter(function(part) { return part; });
      if (pathParts.length >= 2 && pathParts[0] === 'collections') {
        const filterSegments = pathParts.slice(2);
        const allFilterTags = document.querySelectorAll('.collection-filter__tag[data-tag-handle]');
        const tagsByHandle = {};

        allFilterTags.forEach(function(tag) {
          const tagHandle = (tag.getAttribute('data-tag-handle') || '').toLowerCase();
          const tagName = tag.getAttribute('data-tag-name') || '';
          if (tagHandle) tagsByHandle[tagHandle] = tag;
          if (tagName.indexOf('_') > -1) {
            const parts = tagName.split('_');
            if (parts.length > 1) {
              const valuePart = parts.slice(1).join('_');
              const valueHandle = valuePart.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
              tagsByHandle[valueHandle] = tag;
            }
          }
        });

        filterSegments.forEach(function(filterSegment) {
          if (filterSegment === 'page') return;
          const individualFilters = filterSegment.split('+');
          individualFilters.forEach(function(filterHandle) {
            const filterHandleLower = filterHandle.toLowerCase();
            const matchingTag = tagsByHandle[filterHandleLower];
            if (!matchingTag) return;

            var category = 'Filter';
            var filterValue = '';
            const link = matchingTag.querySelector('a');
            if (link) {
              filterValue = link.textContent.trim().replace(/<[^>]*>/g, '').trim().replace(/[●•]/g, '').trim();
            }
            const group = matchingTag.closest('.collection-filter__group');
            if (group) {
              const groupTitleEl = group.querySelector('.collection-filter__group-title span');
              if (groupTitleEl) category = groupTitleEl.textContent.trim();
            }
            if (!filterValue) return;

            const id = ('sidebar-' + category + '-' + filterValue).replace(/\s+/g, '-').toLowerCase();
            var exists = false;
            for (var i = 0; i < filters.length; i++) {
              if (filters[i].id === id) { exists = true; break; }
            }
            if (!exists) {
              filters.push({
                type: 'sidebar',
                category: category,
                value: filterValue,
                element: matchingTag,
                id: id
              });
            }
          });
        });
      }

      // Get sort filter (only if it's not the default/first option)
      const sortSelect = document.querySelector(CONFIG.selectors.sortSelect);
      if (sortSelect && sortSelect.value) {
        const firstOption = sortSelect.querySelector('option:first-child');
        const isDefaultSort = firstOption && sortSelect.value === firstOption.value;
        
        // Only show sort filter if it's not the default option
        if (!isDefaultSort) {
          let sortText = '';
          let sortType = 'sort';
          
          if (sortSelect.value === 'filter.v.availability=1') {
            sortText = 'In Stock Only';
            sortType = 'availability';
          } else {
            const selectedOption = sortSelect.querySelector(`option[value="${sortSelect.value}"]`);
            if (selectedOption) {
              sortText = selectedOption.textContent.trim();
            }
          }
          
          if (sortText) {
            filters.push({
              type: sortType,
              category: sortType === 'availability' ? 'Availability' : 'Sort',
              value: sortText,
              element: sortSelect,
              id: `${sortType}-${sortSelect.value}`.replace(/\s+/g, '-').toLowerCase()
            });
          }
        }
      }

      return filters;
    },

    updateDisplay() {
      const filters = this.getActiveFilters();
      const container = document.querySelector(CONFIG.selectors.selectedFiltersContainer);
      const wrapper = document.querySelector(CONFIG.selectors.selectedFiltersWrapper);
      const list = document.querySelector(CONFIG.selectors.selectedFiltersList);
      
      if (!container || !wrapper || !list) return;

      if (filters.length === 0) {
        // Hide entire container when no filters
        container.classList.remove('has-filters');
        return;
      }

      // Show container and wrapper
      container.classList.add('has-filters');
      
      // Clear existing filters
      list.innerHTML = '';
      
      // Add each filter
      filters.forEach(filter => {
        const filterTag = this.createFilterTag(filter);
        list.appendChild(filterTag);
      });

    },

    createFilterTag(filter) {
      const tag = document.createElement('div');
      tag.className = 'selected-filter-tag filter-type-' + filter.type;
      tag.setAttribute('data-filter-id', filter.id);
      tag.setAttribute('role', 'listitem');

      const labelSpan = document.createElement('span');
      labelSpan.className = 'selected-filter-label';
      labelSpan.textContent = filter.category + ':';

      const valueSpan = document.createElement('span');
      valueSpan.className = 'selected-filter-value';
      valueSpan.textContent = filter.value;

      const removeLabel = 'Remove ' + filter.category + ' ' + filter.value + ' filter';
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'selected-filter-remove';
      removeBtn.setAttribute('aria-label', removeLabel);
      removeBtn.setAttribute('title', removeLabel);
      removeBtn.textContent = '×';

      tag.appendChild(labelSpan);
      tag.appendChild(valueSpan);
      tag.appendChild(removeBtn);

      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeFilter(filter);
        Announcements.announce(filter.category + ' ' + filter.value + ' removed.');
      });
      removeBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          removeBtn.click();
        }
      });
      return tag;
    },

    removeFilter(filter) {
      
      switch (filter.type) {
        case 'sidebar':
          // Click the sidebar filter to deactivate it
          if (filter.element) {
            const link = filter.element.querySelector('a');
            if (link) {
              link.click();
            }
          }
          break;
          
        case 'tab':
          // Click the tab filter to deactivate it
          if (filter.element) {
            filter.element.click();
          }
          break;
          
        case 'sort':
        case 'availability':
          // Reset sort to default (first option)
          const sortSelect = document.querySelector(CONFIG.selectors.sortSelect);
          if (sortSelect) {
            const firstOption = sortSelect.querySelector('option:first-child');
            if (firstOption) {
              sortSelect.value = firstOption.value;
              sortSelect.dispatchEvent(new Event('change'));
            }
          }
          break;
      }
    },

    clearAllFilters() {
      
      // Clear FilterSelectionManager selections first
      if (typeof window.FilterSelectionManager !== 'undefined') {
        window.FilterSelectionManager.clearSelections();
        window.FilterSelectionManager.updateApplyButton();
      }
      
      // Show loading overlay
      LoadingManager.showCollectionLoading();
      
      const UrlUtils = window.FilterUtils && window.FilterUtils.UrlUtils;
      let baseUrl = UrlUtils
        ? UrlUtils.buildClearedCollectionUrl(window.location.href)
        : '/collections/' + window.location.pathname.split('/').filter(Boolean)[1];
      
      // Clear all active, selected, and applied states immediately for visual feedback
      const allSidebarTags = document.querySelectorAll('.collection-filter__tag');
      allSidebarTags.forEach(tag => {
        tag.classList.remove('collection-filter__tag--active', 'collection-filter__tag--selected', 'collection-filter__tag--applied');
        tag.setAttribute('aria-pressed', 'false');
      });
      
      const activeTabFilters = document.querySelectorAll('.filter-collection-box.active--filters');
      activeTabFilters.forEach(box => {
        box.classList.remove('active--filters');
      });
      
      // Reset sort select to default
      const sortSelect = document.querySelector(CONFIG.selectors.sortSelect);
      if (sortSelect) {
        const defaultOption = sortSelect.querySelector('option[value=""]') || 
                             sortSelect.querySelector('option:first-child');
        if (defaultOption) {
          sortSelect.value = defaultOption.value;
        }
      }
      
      // Clear selected filters display
      const selectedFiltersList = document.querySelector(CONFIG.selectors.selectedFiltersList);
      if (selectedFiltersList) {
        selectedFiltersList.innerHTML = '';
      }
      
      const selectedFiltersContainer = document.querySelector(CONFIG.selectors.selectedFiltersContainer);
      if (selectedFiltersContainer) {
        selectedFiltersContainer.classList.remove('has-filters');
      }
      
      // Clear localStorage
      Utils.removeLocalStorage(CONFIG.localStorageKeys.filterValue);
      Utils.removeLocalStorage(CONFIG.localStorageKeys.selectedFilters);
      
      // Use optimized AJAX navigation instead of full page reload
      // Use requestAnimationFrame for async operations to batch with DOM updates
      requestAnimationFrame(async () => {
        try {
          // Update browser URL
          window.history.replaceState(null, null, baseUrl);
          
          // Use AJAX to load clean collection
          if (typeof window.AjaxHandler !== 'undefined') {
            const result = await window.AjaxHandler.getFilteredContent(baseUrl);
    
            if (result && result.collection) {
              const existingCollection = document.querySelector('.collection');
              if (existingCollection) {
                Utils.updateDOMEfficiently(existingCollection, result.collection.innerHTML);
                
                // Update other elements
                if (result.filters) {
                  const existingFilters = document.querySelector('.collection-filter');
                  if (existingFilters) {
                    Utils.updateDOMEfficiently(existingFilters, result.filters.innerHTML);
                  }
                }
                
                // Reinitialize components
                requestAnimationFrame(() => {
                  if (typeof window.AjaxFilter !== 'undefined') {
                    window.AjaxFilter.reinitializeComponents();
                  }
                  
                  // Reinitialize FilterSelectionManager after DOM update
                  if (typeof window.FilterSelectionManager !== 'undefined') {
                    // Clear all selections first
                    window.FilterSelectionManager.clearSelections();
                    // Then reinitialize from the fresh DOM
                    window.FilterSelectionManager.initializeAppliedFilters();
                    window.FilterSelectionManager.updateApplyButton();
                  }
                  
                  LoadingManager.hideCollectionLoading();
                  Announcements.announce('All filters cleared. Showing all products.');
                  document.dispatchEvent(new CustomEvent('filtersCleared', {
                    detail: { url: baseUrl }
                  }));
                });
              }
            } else {
              // Fallback to page reload if AJAX fails
              window.location.href = baseUrl;
            }
          } else {
            // Fallback if AjaxHandler not available
            window.location.href = baseUrl;
          }
        } catch (error) {
          Utils.error('Failed to clear filters via AJAX:', error);
          // Fallback to page reload
          window.location.href = baseUrl;
        }
      }, 100);
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  
  const EventHandlers = {
    init() {
      this.bindShowFilter();
      this.bindCloseFilter();
      this.bindBlurCover();
      this.bindEscapeKey();
      this.bindResize();
    },

    bindShowFilter() {
      const showFilterBtn = document.querySelector(CONFIG.selectors.showFilterBtn);
      if (showFilterBtn) {
        showFilterBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (Utils.isDesktop()) {
            FilterOverlay.setFreeze();
          }
          
          FilterOverlay.show();
          Utils.setLocalStorage(CONFIG.localStorageKeys.filterOpen, 'true');
        });
      } else {
        Utils.error('Show filter button not found with selector: ' + CONFIG.selectors.showFilterBtn);
      }
    },

    bindCloseFilter() {
      document.addEventListener('click', (e) => {
        if (e.target.closest(CONFIG.selectors.closeFilterBtn)) {
          e.preventDefault();
          e.stopPropagation();
          FilterOverlay.hide();
        }
      });
    },

    bindBlurCover() {
      document.addEventListener('click', (e) => {
        if (e.target.matches(CONFIG.selectors.blurCover)) {
          e.preventDefault();
          e.stopPropagation();
          FilterOverlay.hide();
        }
      });
    },

    bindEscapeKey() {
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const filter = document.querySelector(CONFIG.selectors.collectionFilter);
        if (filter && filter.classList.contains('show')) {
          e.preventDefault();
          FilterOverlay.hide();
        }
      });
    },

    bindResize() {
      window.addEventListener('resize', () => {
        // Reinitialize components on resize if needed
        if (typeof window.FilterBar !== 'undefined') {
          window.FilterBar.initializeFilterBarComponents();
        }
      });
    }
  };

  // ========================================
  // EXPOSE TO GLOBAL SCOPE
  // ========================================
  
  window.FilterOverlay = FilterOverlay;
  window.LoadingManager = LoadingManager;
  window.SelectedFiltersManager = SelectedFiltersManager;
  window.EventHandlers = EventHandlers;
  window.FilterAnnouncements = Announcements;

})();
